import { fixCollectionInterval } from '../../core/time-series-data/utils';
import { InsightHistoricalChartSeriesProps } from './insight-historical-chart';
import { useSelectSensors } from '@innovyze/stylovyze';
import * as React from 'react';
import * as SeriesData from '../../core/series-data';
import * as TimeSeriesData from '../../core/time-series-data';
import { getEdgeAutoResolution } from './insight-historical-chart.utils';

// # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # #
// # # # Utilities
// # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # #

const NO_SOURCES_MEMO_ARRAY = [];

export function stringifySource(
  source: TimeSeriesData.Source | Omit<TimeSeriesData.Source, 'key'>
): string {
  if ('key' in source) return source.key;

  let s = `${source.sensorId}`;

  if (source.analytic) {
    if (typeof source.analytic.interval === 'string') {
      s += `:${source.analytic.interval}`;
    } else {
      Object.entries(source.analytic.interval).forEach(([k, v]) => {
        s += `:${k}:${v}`;
      });
    }

    source.analytic.functions.forEach((f) => {
      Object.entries(f).forEach(([k, v]) => {
        s += `:${k}:${v}`;
      });
    });
  }

  return s;
}

// # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # #
// # # # Use sources hook
// # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # #

export function useDataSources(options: {
  series: InsightHistoricalChartSeriesProps[];
  timeSelection: TimeSeriesData.TimeSelection;
}) {
  const { sensors, initialized: sensorsInitialized } = useSelectSensors();

  const [plotDataSources] = SeriesData.useSources<TimeSeriesData.Source>(() => {
    if (!options.series?.length || !sensorsInitialized)
      return NO_SOURCES_MEMO_ARRAY;

    const sources = options.series.flatMap((series) => {
      const { resolution: r, analytic: a, trendlinePeriods = 0 } = series;
      const { referenceDataSource, enableTrendline } = series;
      const { type, customData } = series;
      const sensor = sensors.find((s) => s.sensorId === series.sensorId);
      const ss: Omit<TimeSeriesData.Source, 'key'>[] = [];
      const seconds = fixCollectionInterval(sensor?.collectionInterval);

      const resolution =
        r === 'AUTO'
          ? getEdgeAutoResolution(sensor?.resolutions, options.timeSelection)
          : r;

      const analytic = TimeSeriesData.makeAnalytic(
        resolution,
        type === 'candlestick' ? 'OHLC' : (a ?? 'Close')
      );

      if (customData) return [];

      if (referenceDataSource) {
        ss.push({
          analytic,
          collectionInterval: { seconds },
          sensorId: series.sensorId,
        });
      } else {
        ss.push({
          analytic,
          collectionInterval: { seconds },
          sensorId: series.sensorId,
        });

        if (enableTrendline) {
          ss.push({
            analytic,
            collectionInterval: { seconds },
            sensorId: series.sensorId,
            trendline: trendlinePeriods,
          } as TimeSeriesData.SourceTrendline);
        }
      }

      return ss;
    });

    return sources.map((s) => ({ key: stringifySource(s), ...s }));
  }, [options.series, options.timeSelection, sensors, sensorsInitialized]);

  const plotDataSourcesRef = React.useRef(plotDataSources);

  const [navigatorDataSources] =
    SeriesData.useSources<TimeSeriesData.Source>(() => {
      if (!options.series?.length || !sensorsInitialized)
        return NO_SOURCES_MEMO_ARRAY;

      const sources = options.series.flatMap((series) => {
        const { analytic: a, trendlinePeriods = 0 } = series;
        const { referenceDataSource, enableTrendline } = series;
        const { type, customData } = series;
        const sensor = sensors.find((s) => s.sensorId === series.sensorId);
        const ss: Omit<TimeSeriesData.Source, 'key'>[] = [];
        const seconds = fixCollectionInterval(sensor?.collectionInterval);

        const resolution = 'DAILY';
        const analytic = TimeSeriesData.makeAnalytic(
          resolution,
          type === 'candlestick' ? 'OHLC' : (a ?? 'Close')
        );
        // r === 'AUTO'
        //   ? getEdgeAutoResolution(_sensor?.resolutions, timeSelection)
        //   : s.resolution;

        if (customData) return [];

        if (referenceDataSource) {
          ss.push({
            analytic,
            collectionInterval: { seconds },
            sensorId: series.sensorId,
          });
        } else {
          ss.push({
            analytic,
            collectionInterval: { seconds },
            sensorId: series.sensorId,
          });

          if (enableTrendline) {
            ss.push({
              analytic,
              collectionInterval: { seconds },
              sensorId: series.sensorId,
              trendline: trendlinePeriods,
            } as TimeSeriesData.SourceTrendline);
          }
        }

        return ss;
      });

      return sources.map((s) => ({ key: stringifySource(s), ...s }));
    }, [options.series, sensors, sensorsInitialized]);

  const navigatorDataSourcesRef = React.useRef(navigatorDataSources);

  plotDataSourcesRef.current = plotDataSources;
  navigatorDataSourcesRef.current = navigatorDataSources;

  return {
    plotDataSources,
    plotDataSourcesRef,
    navigatorDataSources,
    navigatorDataSourcesRef,
  };
}
